import React from 'react';
import './Participate.css';

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Heart-shaped flag
const HeartFlag = ({ flagSrc, label, id }) => (
  <div className="heart-flag" role="img" aria-label={label}>
    <svg viewBox="0 0 100 90" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={`heart-clip-${id}`}>
          <path d="M50 85 C 10 60, 0 35, 15 18 C 28 4, 42 8, 50 22 C 58 8, 72 4, 85 18 C 100 35, 90 60, 50 85 Z" />
        </clipPath>
      </defs>
      <image
        href={flagSrc}
        x="0"
        y="0"
        width="100"
        height="90"
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#heart-clip-${id})`}
      />
      <path
        d="M50 85 C 10 60, 0 35, 15 18 C 28 4, 42 8, 50 22 C 58 8, 72 4, 85 18 C 100 35, 90 60, 50 85 Z"
        fill="none"
        stroke="rgba(0,0,0,0.1)"
        strokeWidth="1.5"
      />
    </svg>
  </div>
);

const STUDIES = [
  {
    tag: 'Now Recruiting',
    eyebrow: 'Are you starting IVF?',
    description: 'Please join our study to help us understand how in-vitro fertilization (IVF) affects your cardiovascular and kidney health.',
    involvement: 'Study participation involves 2 short visits to the research lab — 1 before starting IVF, and 1 during IVF treatment.',
    compensation: 'Compensation for your time and free parking are provided.',
    phone: '403-210-7443',
    email: 'kidney.reach@ucalgary.ca',
  },
  {
    tag: 'Now Recruiting',
    eyebrow: 'Thinking about freezing your eggs?',
    description: 'Please join our study to help us understand how freezing your eggs affects your cardiovascular and kidney health.',
    involvement: 'Study participation involves 2 short visits to the research lab — 1 before starting the treatment, and 1 during treatment.',
    compensation: 'Compensation for your time and free parking are provided.',
    phone: '403-210-7443',
    email: 'kidney.reach@ucalgary.ca',
  }
];

const AFFILIATIONS = [
  { name: 'University of Calgary', logo: '/images/affiliations/U_of_C_Logo.png' },
  { name: 'Libin Cardiovascular Institute', logo: '/images/affiliations/Libin_Logo_Red.png' },
  { name: "O'Brien Institute for Public Health", logo: '/images/affiliations/O_Brien_Logo.png' },
];

function Participate() {
  return (
    <div className="page participate-page">
      {/* Banner / Hero */}
      <section className="participate-banner">
        <div className="container">
          <div className="banner-content">
            <span className="banner-eyebrow">Get Involved</span>
            <h1>Participate in Our Research</h1>
            <p className="banner-lede">
              Your voice and experience can help shape the future of
              reproductive, cardiovascular, and kidney research and care.
            </p>
            <a href="#current-studies" className="btn btn-primary banner-cta">
              View Current Studies
            </a>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Why participate */}
        <section className="participate-section why-section">

          <div className="why-grid">
            <div className="why-text">
              <p>
                We welcome all individuals interested in contributing to our
                ongoing research studies. Participants play an essential role
                in advancing knowledge and improving care for future patients.
              </p>
               <p>
                We are committed to creating a safe, supportive, and inclusive
                environment for everyone who takes part — and to ensuring
                every voice is heard and represented in the research we do.
              </p>
            </div>

            <aside className="flags-card" aria-label="We welcome all individuals">
              <p className="flags-card-label">Everyone is welcome</p>
              <div className="flags-row">
                <HeartFlag flagSrc="/images/Nonbinary_flag.svg" label="Non-binary pride" id="nb" />
                <HeartFlag flagSrc="/images/Trans.png" label="Transgender pride" id="trans" />
                <HeartFlag flagSrc="/images/LGBTQ.png" label="LGBTQ+ pride" id="lgbtq" />
              </div>
            </aside>
          </div>
        </section>

        {/* Current studies */}
        <section id="current-studies" className="participate-section studies-section">
          <div className="section-header">
            <span className="section-eyebrow">Now Recruiting</span>
            <h2>Current Studies</h2>
            <p className="section-subtitle">
              We're currently recruiting participants for the following studies.
            </p>
          </div>

          <div className="studies-grid">
            {STUDIES.map((study) => (
              <article key={study.eyebrow} className="study-card">
                <div className="study-card-top">
                  <span className="study-tag">{study.tag}</span>
                  <p className="study-eyebrow">{study.eyebrow}</p>
                </div>

                <div className="study-card-body">
                  <p className="study-description">{study.description}</p>

                  <div className="study-detail">
                    <h4>What's involved</h4>
                    <p>{study.involvement}</p>
                  </div>

                  <div className="study-detail">
                    <h4>Compensation</h4>
                    <p>{study.compensation}</p>
                  </div>
                </div>

                <div className="study-card-contact">
                  <p className="study-contact-label">For more information</p>
                  <div className="study-contact-links">
                    <a href={`tel:${study.phone.replace(/-/g, '')}`} className="study-contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      {study.phone}
                    </a>
                    <a href={`mailto:${study.email}`} className="study-contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      {study.email}
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* What to expect */}
        <section className="participate-section expect-section">
          <div className="section-header">
            <span className="section-eyebrow">The Process</span>
            <h2>What to Expect</h2>
            <p className="section-subtitle">
              Participating in research should feel approachable. Here's how it
              typically works from the first email to the end of your study visits.
            </p>
          </div>

          <div className="expect-steps">
            <div className="expect-step">
              <div className="expect-step-number">01</div>
              <h3>Reach Out</h3>
              <p>
                Contact the study coordinator by phone or email. Ask any questions
                you have — there's no commitment at this stage.
              </p>
            </div>
            <div className="expect-step">
              <div className="expect-step-number">02</div>
              <h3>Hear More &amp; Decide</h3>
              <p>
                We walk you through the study purpose, what's involved, and the
                time commitment so you can make an informed decision.
              </p>
            </div>
            <div className="expect-step">
              <div className="expect-step-number">03</div>
              <h3>Study Visits</h3>
              <p>
                If you choose to participate, you'll attend the study visits
                described for each study. Compensation and free parking are provided.
              </p>
            </div>
            <div className="expect-step">
              <div className="expect-step-number">04</div>
              <h3>Learn About the Results</h3>
              <p>
                Your participation helps advance scientific knowledge. 
                Once the study is completed, we will share the study results directly with our participants, in addition to the broader community.
              </p>
            </div>
          </div>
        </section>

        {/* General contact CTA */}
        <section className="participate-cta-card">
          <h2>
            <span className="heading-icon"><ChatIcon /></span>
            Have general questions?
          </h2>
          <p>
            For general inquiries about our research or to express interest in
            future studies, please reach out to our team. We are happy to help.
          </p>
          <div className="participate-contact">
            <a href="mailto:kidney.reach@ucalgary.ca" className="btn btn-primary">
              Email Us
            </a>
            <a href="tel:+14032109610" className="btn btn-secondary">
              Call 403-210-9610
            </a>
          </div>
        </section>

        {/* Affiliations */}
        <section className="affiliations-section">
          <div className="affiliations-list">
            {AFFILIATIONS.map((affiliation) => (
              <div key={affiliation.name} className="affiliation-logo">
                <img
                  src={affiliation.logo}
                  alt={affiliation.name}
                  title={affiliation.name}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Participate;